import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import Header from '../components/Header';
import ErrorRetry from '../components/ErrorRetry';
import EmptyState from '../components/EmptyState';
import TableSkeleton from '../components/TableSkeleton';
import { fetchCustomersRFM } from '../api/customersRfm';
import { currentUser } from '../api/auth';
import { t } from '../lib/i18n';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import type { CustomersRFMPayload, RFMSegment, User } from '../api/types';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

const SEGMENT_COLOR: Record<RFMSegment, 'emerald' | 'blue' | 'sky' | 'rose' | 'amber' | 'gray' | 'slate'> = {
  champions: 'emerald',
  loyal: 'blue',
  recent: 'sky',
  big_at_risk: 'rose',
  at_risk: 'amber',
  dormant: 'gray',
  regular: 'slate',
};

const WINDOWS = [
  { days: 90, label: t.customers.window.d90 },
  { days: 180, label: t.customers.window.d180 },
  { days: 365, label: t.customers.window.d365 },
];

export default function Customers() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === 'staff') return <Navigate to="/" replace />;
  return <CustomersInner user={user} />;
}

function CustomersInner({ user }: { user: User }) {
  const [days, setDays] = useState(365);
  const [data, setData] = useState<CustomersRFMPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [segFilter, setSegFilter] = useState<RFMSegment | 'all'>('all');
  // App-wide store selection (header picker); the RFM walk is per store.
  const [sid] = useStoreFilter(user);

  const load = () => {
    setErr(null);
    setData(null);
    fetchCustomersRFM(days, sid)
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [days, sid]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.customers.filter((c) => {
      if (segFilter !== 'all' && c.segment !== segFilter) return false;
      if (!needle) return true;
      return String(c.anum).includes(needle) || c.name.toLowerCase().includes(needle);
    });
  }, [data, q, segFilter]);
  const pg = usePagination(filtered);
  // The table lives in its own scroll box — a page change should start at the top.
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = 0; }, [pg.page]);

  const kpis = useMemo(() => {
    if (!data) return { revenue: 0, atRisk: 0, churn: 0 };
    let revenue = 0, atRisk = 0, churn = 0;
    for (const c of data.customers) {
      revenue += c.revenue_mxn;
      if (c.segment === 'at_risk' || c.segment === 'big_at_risk') atRisk++;
      if (c.churn_risk) churn++;
    }
    return { revenue, atRisk, churn };
  }, [data]);

  return (
    <div className="min-h-full">
      <Header user={user} storeFilter />
      <main className="p-4 sm:p-6 space-y-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Title>{t.customers.title}</Title>
            <Text className="text-tremor-content text-sm">{t.customers.subtitle}</Text>
          </div>
          <div className="flex gap-1">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                onClick={() => setDays(w.days)}
                className={'text-sm px-3 py-1.5 rounded-tremor-default border ' +
                  (days === w.days
                    ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                    : 'bg-tremor-background text-tremor-content border-tremor-border hover:bg-tremor-background-muted')}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {err && <ErrorRetry message={err} onRetry={load} />}
        {!err && data === null && <TableSkeleton rows={8} cols={8} />}

        {!err && data !== null && (
          <>
            <Card className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi label={t.customers.kpi.customers} value={String(data.total_customers)} />
              <Kpi label={t.customers.kpi.revenue} value={fmtMxn.format(kpis.revenue)} />
              <Kpi label={t.customers.kpi.atRisk} value={String(kpis.atRisk)} />
              <Kpi label={t.customers.kpi.churn} value={String(kpis.churn)} />
            </Card>

            {/* Segment chips double as filters. */}
            <div className="flex flex-wrap gap-2">
              <SegChip
                active={segFilter === 'all'}
                onClick={() => setSegFilter('all')}
                label={t.customers.segmentAll}
                detail={String(data.total_customers)}
              />
              {data.segments.map((s) => (
                <SegChip
                  key={s.segment}
                  active={segFilter === s.segment}
                  onClick={() => setSegFilter(segFilter === s.segment ? 'all' : s.segment)}
                  label={t.customers.segments[s.segment]}
                  detail={`${s.count} · ${fmtMxn.format(s.revenue_mxn)}`}
                  title={t.customers.segmentHints[s.segment]}
                />
              ))}
            </div>

            <TextInput
              value={q}
              onValueChange={setQ}
              placeholder={t.customers.searchPlaceholder}
              className="max-w-sm"
            />

            {(data.excluded_anums?.length ?? 0) > 0 && (
              <Text className="text-xs text-tremor-content">
                {t.customers.excludedNote.replace('{list}', data.excluded_anums!.join(', '))}
              </Text>
            )}

            {filtered.length === 0 ? (
              <EmptyState message={t.customers.empty} />
            ) : (
              <Card className="p-0 overflow-hidden">
                <div ref={boxRef} className="overflow-x-auto overflow-y-auto print-expand" style={{ maxHeight: '65vh' }}>
                  <Table>
                    <TableHead className="sticky top-0 bg-tremor-background z-10 shadow-sm">
                      <TableRow>
                        <TableHeaderCell className="whitespace-nowrap">{t.customers.cols.account}</TableHeaderCell>
                        <TableHeaderCell className="whitespace-nowrap">{t.customers.cols.segment}</TableHeaderCell>
                        <TableHeaderCell className="whitespace-nowrap">{t.customers.cols.rfm}</TableHeaderCell>
                        <TableHeaderCell className="text-right whitespace-nowrap">{t.customers.cols.tickets}</TableHeaderCell>
                        <TableHeaderCell className="text-right whitespace-nowrap">{t.customers.cols.revenue}</TableHeaderCell>
                        <TableHeaderCell className="whitespace-nowrap">{t.customers.cols.lastBuy}</TableHeaderCell>
                        <TableHeaderCell className="text-right whitespace-nowrap">{t.customers.cols.recency}</TableHeaderCell>
                        <TableHeaderCell className="text-right whitespace-nowrap">{t.customers.cols.cadence}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pg.pageRows.map((c) => (
                        <TableRow key={c.anum}>
                          <TableCell className="whitespace-nowrap">
                            <DrillCell target={{ kind: 'account', anum: c.anum }}>
                              {c.anum} {c.name && `· ${c.name}`}
                            </DrillCell>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge color={SEGMENT_COLOR[c.segment]}>{t.customers.segments[c.segment]}</Badge>
                            {c.churn_risk && (
                              <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                                ⚠ {t.customers.churnBadge}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">{c.r}·{c.f}·{c.m}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.tickets}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtMxn.format(c.revenue_mxn)}</TableCell>
                          <TableCell className="whitespace-nowrap">{c.last_date}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.recency_days}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.cadence_days > 0 ? c.cadence_days.toFixed(0) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination {...pg} className="px-4" />
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function SegChip({ active, onClick, label, detail, title }: {
  active: boolean; onClick: () => void; label: string; detail: string; title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={'text-sm px-3 py-1.5 rounded-tremor-default border ' +
        (active
          ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
          : 'bg-tremor-background text-tremor-content border-tremor-border hover:bg-tremor-background-muted')}
    >
      {label} <span className={active ? 'opacity-80' : 'text-tremor-content-subtle'}>· {detail}</span>
    </button>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
